import React, { useEffect, useState } from 'react';
import { Anchor, Button, Modal, Panel, UnorderedList, UnorderedListItem } from '@keplerco/core';
import classNames from 'classnames';
import layoutStyles from '../../../../design/layout.module.css';
import { SkeletonOverlay } from '../../../../components/general/loading-state/skeleton-overlay';
import { WidgetCard } from '../../../analytics/analytics-filters/widget-card';
import { useAppActions, useAppState } from '../../../../overmind';
import { NotificationPriority, NotificationType } from '../../../../notifications/notifications.models';
import { IAssessorsStepLayoutProps } from './assessors-step.models';
import { ConfirmationModalLayout, ExplainerFocusPanelLayout } from '../../../../widgets/layouts';
import assessmentsWizardStyles from '../../assessments-wizard.module.css';
import { useKeplerNavigate } from '../../../../navigation/guards/use-kepler-navigate';
import { TertiaryButton } from '../../../../components/general/tertiary-button/tertiary-button';
import { IncludeEntityCard } from '../include-entity-card';
import { AssessorsSelectionList } from './assessors-selection-list';
import { SelectionPanel } from '../../../../components/panels/selection/selection.panel';
import { AssessorsAccordion } from '../rating-assessment-accordion/assessors-accordion';
import { PeerAssessmentAssigneeResponse, PeerAssessmentRequest, PeerAssessmentResponse } from '../../../../overmind/actions';
import { RatingAssessmentEmptyStateImage } from '../rating-assessment-empty-state.image';
import { EmptyState } from '../../../../components/general/empty-state/empty-state';
import { ExitModal } from '../../../../components/modals/exit.modal';
import { PagePath } from '../../../../navigation/navigation.enums';

export function AssessorsStepLayout({ isLoading, setIsLoading, assessmentSlug, onBack, onNext, ...props }: IAssessorsStepLayoutProps): JSX.Element {
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [peerAssessment, setPeerAssessment] = useState<PeerAssessmentResponse>(props.peerAssessment);
  const [isExplainerPanelOpen, setIsExplainerPanelOpen] = useState(false);
  const [isIncludingManagers, setIsIncludingManagers] = useState<boolean>(false);
  const [selectedAssignee, setSelectedAssignee] = useState<PeerAssessmentAssigneeResponse>();
  const [isSelectionPanelOpen, setIsSelectionPanelOpen] = React.useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isSaveDraft, setIsSaveDraft] = useState<boolean | null>(null);

  useEffect(() => {
    if (isSaveDraft === null) return; 
  
    if (isSaveDraft) {
      actions.dismissAllNotifications();
      actions.addNotification({
        id: crypto.randomUUID(),
        active: true,
        priority: NotificationPriority.Toast,
        toastIncludeIcon: true,
        type: NotificationType.Warn,
        title: 'This assessment is now a draft',
        message: 'You will need to schedule this once your changes are complete',
      });
      keplerNavigate(
        `${PagePath.assessmentManagementBase}${PagePath.assessmentManagement.replace(':companySlug', companyVariables.slug!)}`
      );
    } else {
      onNext(peerAssessment);
    }
  
    setIsSaveDraft(null);
    setIsLoading(false);
  }, [isSaveDraft]);

  useEffect(() => {
    if (hasUnsavedChanges) return;
    setHasUnsavedChanges(JSON.stringify(peerAssessment.assignees) !== JSON.stringify(props.peerAssessment.assignees));
  }, [peerAssessment]);

  function includeSelfAssessment() {
    setPeerAssessment(currentState => {
      return {
        ...currentState,
        assignees: currentState.assignees.map(assignee => {
          const selfAssessorExists = assignee.reviewers.some(reviewer => reviewer.learnerSlug === assignee.learnerSlug);

          if (selfAssessorExists) {
            return assignee;
          }

          return {
            ...assignee,
            reviewers: [
              ...assignee.reviewers,
              {
                department: assignee.department,
                email: assignee.email,
                fullName: assignee.fullName,
                learnerSlug: assignee.learnerSlug,
                roles: assignee.roles,
                team: assignee.team,
                isSelf: true,
                isManager: false,
              },
            ],
          };
        }),
      };
    });
    actions.dismissAllNotifications();
    actions.addNotification({
      active: true,
      id: crypto.randomUUID(),
      type: NotificationType.Success,
      title: 'Self Assessment Added',
      message: 'Subjects will be given the chance to rate their own skills',
      priority: NotificationPriority.Toast,
      toastIncludeIcon: true,
    });
  }

  async function includeManagerAssessment() {
    setIsIncludingManagers(true);

    const response = await actions.getPeerAssessmentAssigneesManagers(assessmentSlug);
    setPeerAssessment(currentState => {
      const nextState: PeerAssessmentResponse = structuredClone(currentState);
      response?.forEach(assignee => {
        const assigneeIndex = nextState.assignees.findIndex(a => a.learnerSlug === assignee.learnerSlug);
        if (assigneeIndex === -1) return;

        assignee.managers.forEach(manager => {
          const reviewerIndex = nextState.assignees[assigneeIndex].reviewers.findIndex(reviewer => reviewer.learnerSlug === manager.learnerSlug);
          if (reviewerIndex === -1) {
            nextState.assignees[assigneeIndex].reviewers.push(manager);
          } else {
            nextState.assignees[assigneeIndex].reviewers[reviewerIndex].isManager = true;
          }
        });
      });
      return nextState;
    });

    actions.dismissAllNotifications();
    actions.addNotification({
      active: true,
      id: crypto.randomUUID(),
      type: NotificationType.Success,
      title: 'Managers Added',
      message: 'Line managers have been included in the list of assessors',
      priority: NotificationPriority.Toast,
      toastIncludeIcon: true,
    });

    setIsIncludingManagers(false);
  }

  function removeAllAssessors() {
    setPeerAssessment(currentState => {
      const nextState: PeerAssessmentResponse = structuredClone(currentState);
      nextState.assignees.forEach(assignee => (assignee.reviewers = []));
      return nextState;
    });
    setIsConfirmationModalOpen(false);
  }

  async function saveAssessors(isSaveDraft?: boolean) {
    setIsLoading(true);

    const request: PeerAssessmentRequest = {
      ...peerAssessment,
      assignees: peerAssessment.assignees.map(assignee => ({
        ...assignee,
        reviewers: assignee.reviewers.map(reviewer => ({ learnerSlug: reviewer.learnerSlug, isManager: reviewer.isManager })),
        companySkillIds: assignee.companySkills.map(companySkill => companySkill.id),
      })),
    };
    await actions.updatePeerAssessment({ assessmentSlug, peerAssessment: request });

    setHasUnsavedChanges(false);
    setIsSaveDraft(isSaveDraft ?? false);
  }

  return (
    <React.Fragment>
      <div className={assessmentsWizardStyles.stepContainer}>
        <SkeletonOverlay isLoading={isLoading}>
          <WidgetCard onHelpClick={() => setIsExplainerPanelOpen(true)} title="Assessors" subtitle="Select the people you would like to have assess the selected subjects">
            <div className={assessmentsWizardStyles.stepActionCards}>
              <IncludeEntityCard title="Manager assessment" subtitle="The subject's line manager will be added to the assessors" action={includeManagerAssessment} isLoading={isIncludingManagers} />
              <IncludeEntityCard title="Self assessment" subtitle="Subjects will be added to their list of assessors" action={includeSelfAssessment} />
              <IncludeEntityCard buttonText="Select" title="Assessors for all subjects" subtitle="Subjects will share the same assessor(s)" action={() => setIsSelectionPanelOpen(true)} />
            </div>

            {!!peerAssessment.assignees.length ? (
              <React.Fragment>
                <div className={assessmentsWizardStyles.stepBodyHeading}>
                  <h4>Add or edit the assessors for each subject</h4>

                  <TertiaryButton type="button" style={{ marginLeft: 'auto' }} disabled={!peerAssessment.assignees.some(assignee => !!assignee.reviewers.length)} onClick={() => setIsConfirmationModalOpen(true)}>
                    Remove all assessors
                  </TertiaryButton>
                </div>

                {peerAssessment.assignees?.map(assignee => (
                  <AssessorsAccordion
                    key={assignee.learnerSlug}
                    assignee={assignee}
                    onAdd={() => setSelectedAssignee(assignee)}
                    onRemove={learnerSlug =>
                      setPeerAssessment(currentState => {
                        const nextState: PeerAssessmentResponse = structuredClone(currentState);
                        const index = nextState.assignees.findIndex(a => a.learnerSlug === assignee.learnerSlug);
                        nextState.assignees[index].reviewers = nextState.assignees[index].reviewers.filter(reviewer => reviewer.learnerSlug !== learnerSlug);
                        return nextState;
                      })
                    }
                  />
                ))}
              </React.Fragment>
            ) : (
              <EmptyState title="" subtitle="The selected subjects will display here with their assessors" imageElement={<RatingAssessmentEmptyStateImage />} />
            )}
          </WidgetCard>
        </SkeletonOverlay>

        <footer className={classNames('card', layoutStyles.wizardFooter)}>
          <Anchor reverse arrow aria-label="Return to previous step" onClick={onBack}>
            Back
          </Anchor>

          <div className={assessmentsWizardStyles.stepFooterButtonGroup}>
            <Anchor disabled={isLoading} aria-label="Save as draft and exit" onClick={() => saveAssessors(true)}>
              Save draft
            </Anchor>

            <Button disabled={isLoading} filled type="button" arrow aria-label="Save and continue to next step" onClick={() => saveAssessors()}>
              Next
            </Button>
          </div>
        </footer>
      </div>

      <Panel open={!!isExplainerPanelOpen} onClose={() => setIsExplainerPanelOpen(false)}>
        <ExplainerFocusPanelLayout title="Understanding assessors" subtitle="Assessors are the people providing feedback on each subject's skills" onClose={() => setIsExplainerPanelOpen(false)}>
          <React.Fragment>
            <p>An assessor gives ratings and comments on a subject's skills. Assessors can be colleagues, line managers, or anyone that you choose to include</p>
            <br />
            <h3>How it works</h3>
            <br />
            <p className="bold" style={{ marginBottom: 8 }}>
              You can assign assessors in three ways:
            </p>
            <UnorderedList gap="8px">
              <UnorderedListItem>Self-assessment - the subject rates their own skills</UnorderedListItem>
              <UnorderedListItem>Line manager assignment - the subject's manager(s) are added automatically</UnorderedListItem>
              <UnorderedListItem>Manual selection - choose any employee to assess a specific subject</UnorderedListItem>
            </UnorderedList>
            <br />
            <p>Each subject can have a unique set of assessors. Assessments are one-directional: subjects do not rate their assessors.</p>
          </React.Fragment>
        </ExplainerFocusPanelLayout>
      </Panel>

      <Panel
        open={isSelectionPanelOpen || !!selectedAssignee}
        onClose={() => {
          setSelectedAssignee(undefined);
          setIsSelectionPanelOpen(false);
        }}
        size="fullscreen"
      >
        {(isSelectionPanelOpen || !!selectedAssignee) && (
          <SelectionPanel
            title={selectedAssignee ? `Select ${selectedAssignee.fullName}'s assessors` : 'Select assessors for all subjects'}
            subtitle={selectedAssignee ? `Select who will assess ${selectedAssignee.fullName}'s skills` : `Select who will assess each subject's skills`}
            selectedEntities={!!selectedAssignee ? selectedAssignee.reviewers.map(reviewer => ({ slug: reviewer.learnerSlug, name: reviewer.fullName, entity: reviewer })) : []}
            entityType="assessors"
            onCancel={() => {
              setSelectedAssignee(undefined);
              setIsSelectionPanelOpen(false);
            }}
            onAdd={selectedEntities => {
              const reviewers = selectedEntities.map(selectedEntity => selectedEntity.entity);
              setPeerAssessment(currentState => {
                const nextState: PeerAssessmentResponse = structuredClone(currentState);
                if (!!selectedAssignee) {
                  const index = nextState.assignees.findIndex(assignee => assignee.learnerSlug === selectedAssignee.learnerSlug);
                  nextState.assignees[index].reviewers = reviewers;
                } else {
                  nextState.assignees.forEach(assignee => {
                    assignee.reviewers.push(...reviewers.filter(reviewer => !assignee.reviewers.some(assigneeReviewer => assigneeReviewer.learnerSlug === reviewer.learnerSlug)));
                  });
                }
                return nextState;
              });
              setSelectedAssignee(undefined);
              setIsSelectionPanelOpen(false);
            }}
          >
            {({ selectedEntities, setSelectedEntities }) => <AssessorsSelectionList selectedEntities={selectedEntities} setSelectedEntities={setSelectedEntities} />}
          </SelectionPanel>
        )}
      </Panel>

      <Modal open={isConfirmationModalOpen} onClose={() => setIsConfirmationModalOpen(false)}>
        <ConfirmationModalLayout title="Are you sure you want to remove all assessors from this assessment?" submitButtonText="Yes, remove" cancelButtonText="No, cancel" onClickSubmit={removeAllAssessors} onClickCancel={() => setIsConfirmationModalOpen(false)} />
      </Modal>

      <ExitModal hasUnsavedChanges={hasUnsavedChanges} />
    </React.Fragment>
  );
}
